import { apiUrl } from "../config/env";

/**
 * The dealbid-core client.
 *
 * Two canon rules shape this file (docs/SECURITY.md):
 *
 * 1. **The session is a cookie this bundle cannot read.** `dealbid-core` issues an
 *    HttpOnly, Secure, SameSite cookie at the end of a Google sign-in (canon decision
 *    `D-008`), so every request opts into `credentials: "include"` and the app never
 *    holds, stores or forwards a credential of its own. Core must answer the seller app's
 *    origin with `Access-Control-Allow-Credentials` and an explicit origin — never `*`.
 * 2. **Nothing sensitive goes in a URL.** Secrets travel in a JSON body, because a query
 *    string reaches access logs, browser history and `Referer` headers.
 *
 * Nothing here logs. A session must never reach a console, and `no-console` in
 * eslint.config.js keeps that true.
 */

/** The canon error envelope from contracts/API.md. */
interface ErrorEnvelope {
  error?: {
    code?: unknown;
    message?: unknown;
    requestId?: unknown;
    fields?: unknown;
  };
}

/**
 * One field core refused, from the `fields` array a `VALIDATION_FAILED` carries.
 *
 * `contracts/API.md` ships this array "so a form can mark the exact inputs that failed".
 * Dropping it would leave a founder with a whole-form error and no way to know which of a
 * dozen inputs core objected to — so it is carried on the error rather than flattened into
 * a sentence. `field` is `""` for a refusal about the body as a whole (an empty patch), and
 * that is a value, not an omission: it names the request rather than any one input.
 */
export interface FieldIssue {
  field: string;
  code: string;
  message: string;
}

export class ApiError extends Error {
  /** HTTP status, or `null` when the request never completed. */
  readonly status: number | null;
  /** Machine-readable code from the canon error envelope, when core sent one. */
  readonly code: string | null;
  /** Core's request ID, useful for support without exposing anything sensitive. */
  readonly requestId: string | null;
  /** The offending inputs when core answered `VALIDATION_FAILED`; empty otherwise. */
  readonly fields: readonly FieldIssue[];

  constructor(
    message: string,
    options: {
      status?: number | null;
      code?: string | null;
      requestId?: string | null;
      fields?: readonly FieldIssue[] | undefined;
    } = {},
  ) {
    super(message);
    this.name = "ApiError";
    this.status = options.status ?? null;
    this.code = options.code ?? null;
    this.requestId = options.requestId ?? null;
    this.fields = options.fields ?? [];
  }
}

/** The request never reached core: offline, DNS, TLS, CORS or a timeout. */
export function isNetworkError(error: unknown): boolean {
  return error instanceof ApiError && error.status === null;
}

/**
 * Core rejected the caller's identity. Both statuses are treated the same way by the
 * seller app: whatever the reason, this browser has no valid seller session.
 */
export function isUnauthorized(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 403);
}

const REQUEST_TIMEOUT_MS = 15_000;

/** Not every runtime the suite runs in implements `AbortSignal.timeout`. */
function timeoutSignal(ms: number = REQUEST_TIMEOUT_MS): AbortSignal | undefined {
  if (typeof AbortSignal === "undefined" || typeof AbortSignal.timeout !== "function") {
    return undefined;
  }
  return AbortSignal.timeout(ms);
}

/**
 * The `fields` array, parsed rather than cast.
 *
 * An error body is as untrusted as a success body, and this one is rendered directly
 * against a founder's inputs. An entry missing its `field` or its `message` is dropped
 * instead of being shown as a blank problem beside a box, and a body whose `fields` is not
 * an array simply yields none — the whole-request message still reaches the founder.
 */
function readFieldIssues(value: unknown): FieldIssue[] {
  if (!Array.isArray(value)) return [];
  const issues: FieldIssue[] = [];
  for (const entry of value) {
    if (typeof entry !== "object" || entry === null) continue;
    const row = entry as Record<string, unknown>;
    const field = typeof row.field === "string" ? row.field : null;
    const message = typeof row.message === "string" ? row.message.trim() : "";
    if (field === null || message === "") continue;
    issues.push({
      field,
      code: typeof row.code === "string" ? row.code : "",
      message,
    });
  }
  return issues;
}

function readErrorEnvelope(body: unknown): {
  code: string | null;
  message: string | null;
  requestId: string | null;
  fields: FieldIssue[];
} {
  const envelope = (body ?? {}) as ErrorEnvelope;
  const error = envelope.error;
  return {
    code: typeof error?.code === "string" ? error.code : null,
    message: typeof error?.message === "string" ? error.message : null,
    requestId: typeof error?.requestId === "string" ? error.requestId : null,
    fields: readFieldIssues(error?.fields),
  };
}

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PATCH";
  /** Serialised as JSON. Sensitive values belong here, never in the path. */
  body?: unknown;
  /**
   * Send this request **without** the session cookie.
   *
   * The seller surface is credentialed and this is the single exception:
   * `GET /v1/submission-terms` is a *public* read (CORE-016), and
   * `contracts/API.md` § Public cross-origin policy states that public endpoints "carry no
   * credentials, so `Access-Control-Allow-Credentials` is never sent on them". A browser
   * discards a credentialed response that lacks that header, so asking for the wording with
   * `credentials: "include"` would fail in a browser however the allow-list is configured.
   * Omitting them is therefore not a relaxation — it is the only way to read a public
   * endpoint at all, and it sends nothing rather than something.
   */
  anonymous?: boolean;
}

/** A completed `/v1` request: the parsed body and the status core answered with. */
export interface ApiResponse<T> {
  /** HTTP status. Some routes distinguish two successes — `201` created, `200` updated. */
  status: number;
  body: T;
}

/**
 * Performs one `/v1` request and returns the parsed JSON body **with** its status.
 *
 * Throws {@link ApiError} for every failure — transport, status and malformed body alike —
 * so callers branch on one error type instead of three.
 */
export async function apiRequestWithStatus<T>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<ApiResponse<T>> {
  const { method = "GET", body, anonymous = false } = options;

  const headers: Record<string, string> = { Accept: "application/json" };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const signal = timeoutSignal();
  const init: RequestInit = {
    method,
    headers,
    // The seller session cookie is cross-origin (app.dealbid.app -> api.dealbid.app).
    // A public read is the one call that must not carry it — see `anonymous` above.
    credentials: anonymous ? "omit" : "include",
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  };

  let response: Response;
  try {
    response = await fetch(apiUrl(path), init);
  } catch {
    // The original error is deliberately not attached: it can carry the request URL, and
    // this app must never surface or record request detail.
    //
    // Every path this app calls now carries the session cookie and has no Cloudflare edge
    // rule of its own, so a transport failure has one meaning again. The probe that used
    // to tell an unreadable edge `429` apart from an outage went with the anonymous token
    // exchange that was its only caller (canon decision `D-008`); sign-in is a top-level
    // navigation now, and a browser mid-navigation reads core's answer for itself.
    throw new ApiError("Dealbid could not be reached.", { status: null });
  }

  if (response.status === 204) return { status: 204, body: undefined as T };

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const { code, message, requestId, fields } = readErrorEnvelope(payload);
    throw new ApiError(message ?? `Request failed with status ${response.status}.`, {
      status: response.status,
      code,
      requestId,
      fields,
    });
  }

  return { status: response.status, body: payload as T };
}

/** The same request, for the routes whose success status carries no information. */
export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  return (await apiRequestWithStatus<T>(path, options)).body;
}
